import { Paper, Typography } from '@material-ui/core'
import { memo } from 'react'

import { CurrencyTotal } from '../../graphql/promotions'
import {
  formatCount,
  formatMinorWithCurrency
} from '../../helpers/promotionAmount'
import { getTranslate } from '../../localization'
import useStyles from './styles/OverviewTotals.styles'

interface OverviewTotalsProps {
  totalAppliedCount: number
  totalInFlightCount: number
  discountTotals: CurrencyTotal[]
  inFlightDiscountTotals: CurrencyTotal[]
}

/**
 * The roll-up across the whole filtered set.
 *
 * ONE MONEY FIGURE PER CURRENCY, never a grand total, and this is a contract constraint rather
 * than a layout preference. `discountMinor` is minor units of a per-promotion currency and the
 * scale differs by currency — IRR 0, USD 2, IQD 3 — so summing the integers adds one rial to
 * one cent: a number with no unit, not an approximation. Converting instead would mint a
 * financial figure from an FX authority this platform does not have.
 *
 * A currency with no activity is absent from the server's response rather than present as
 * zero, so this renders what came back and does not pad the list to a fixed set.
 *
 * COUNTS ARE SCALAR and deliberately so: a count is dimensionless, so it is the one figure
 * here that may legitimately sum across currencies. Rendering them beside per-currency money
 * makes that asymmetry visible instead of leaving a reader to wonder why one is split.
 */
const OverviewTotals: React.FC<OverviewTotalsProps> = ({
  totalAppliedCount,
  totalInFlightCount,
  discountTotals,
  inFlightDiscountTotals
}) => {
  const classes = useStyles()

  return (
    <div className={classes.totals}>
      <Paper className={classes.card} elevation={0} data-cy="OverviewApplied">
        <Typography variant="body2" className={classes.label}>
          {getTranslate('Applied')}
        </Typography>
        <Typography variant="h4" className={classes.count}>
          {formatCount(totalAppliedCount)}
        </Typography>
        {discountTotals.length === 0 ? (
          <Typography variant="body2" className={classes.empty}>
            {getTranslate('No discount given yet')}
          </Typography>
        ) : (
          discountTotals.map((total) => (
            <Typography
              key={total.currency}
              variant="body1"
              className={classes.money}>
              {formatMinorWithCurrency(
                total.discountMinor,
                total.currency,
                total.minorUnitDigits
              )}
            </Typography>
          ))
        )}
      </Paper>

      {/*
        In flight is a SEPARATE card, not a second line inside the one above.

        A reservation can still be released, so folding it into the given-away figure would
        overstate what has actually left the provider's margin — the server keeps the two
        lists apart for that reason and the screen has to as well. Adjacent rather than
        stacked so the comparison is available without implying the two add up.
      */}
      <Paper className={classes.card} elevation={0} data-cy="OverviewInFlight">
        <Typography variant="body2" className={classes.label}>
          {getTranslate('In flight')}
        </Typography>
        {/*
          Said on screen, not only in this file's comments.

          The two cards are stylistically identical and adjacent, each leading with a large
          count, and the default reading of two adjacent totals is that they sum. The docblock
          above argues adjacency enables comparison without implying addition — but that
          argument lives in the source, and a reader does not have it. The mutation test
          guards the DATA; this guards the READING.
        */}
        <Typography variant="caption" className={classes.caption}>
          {getTranslate('Not included in Applied. May still be released.')}
        </Typography>
        <Typography variant="h4" className={classes.count}>
          {formatCount(totalInFlightCount)}
        </Typography>
        {inFlightDiscountTotals.length === 0 ? (
          <Typography variant="body2" className={classes.empty}>
            {getTranslate('Nothing reserved')}
          </Typography>
        ) : (
          inFlightDiscountTotals.map((total) => (
            <Typography
              key={total.currency}
              variant="body1"
              className={classes.money}>
              {formatMinorWithCurrency(
                total.discountMinor,
                total.currency,
                total.minorUnitDigits
              )}
            </Typography>
          ))
        )}
      </Paper>
    </div>
  )
}

export default memo(OverviewTotals)
